import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Radar, Loader2, Lock, ArrowRight, RefreshCw, Eye, Search, Activity, AlertTriangle,
  Gauge, Clock, TrendingUp, TrendingDown, CircleHelp,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import HelpPopover from "@/components/HelpPopover";

const TIER_META = {
  DESERVES_ATTENTION: { label: "Deserves Attention", cls: "text-tc-coach border-tc-coach/40", dot: "bg-tc-coach" },
  DEVELOPING: { label: "Developing", cls: "text-tc-xp border-tc-xp/40", dot: "bg-tc-xp" },
  WATCH: { label: "Watching", cls: "text-tc-mastery border-tc-mastery/40", dot: "bg-tc-mastery" },
};

export default function Intelligence() {
  const navigate = useNavigate();
  const [authz, setAuthz] = useState(null);
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [opening, setOpening] = useState(null);
  const [whyNot, setWhyNot] = useState({ open: false, symbol: "", result: null });

  useEffect(() => {
    api.get("/intel/authorization").then((r) => setAuthz(r.data)).catch(() => setAuthz({ authorization: { authorized: false, reason: "Unable to check authorization." } }));
    loadQueue();
  }, []);

  const loadQueue = () => api.get("/intel/queue").then((r) => setData(r.data)).catch(() => {});

  const runScan = async () => {
    setScanning(true);
    try { await api.post("/intel/scan"); await loadQueue(); toast.success("Market scan complete"); }
    catch (e) { toast.error(apiErr(e)); }
    setScanning(false);
  };

  const openCandidate = async (cid) => {
    setOpening(cid);
    try {
      const { data: r } = await api.post("/analyst/analyze-candidate", { candidate_id: cid });
      navigate(`/analyst?assessment=${r.assessment_id}`);
    } catch (e) { toast.error(apiErr(e)); setOpening(null); }
  };

  const watch = async (cid) => {
    try { await api.post("/intel/watch", { candidate_id: cid }); toast.success("Added to your watch list"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doWhyNot = async () => {
    if (!whyNot.symbol) return;
    setWhyNot((w) => ({ ...w, result: "loading" }));
    try {
      const { data: r } = await api.get(`/intel/why-not?symbol=${encodeURIComponent(whyNot.symbol)}`);
      setWhyNot((w) => ({ ...w, result: r }));
    } catch (e) { setWhyNot((w) => ({ ...w, result: { reason: apiErr(e) } })); }
  };

  if (authz && !authz.authorization.authorized) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="tc-card p-8 text-center border-tc-mastery/25" data-testid="intel-gated">
          <Lock className="w-8 h-8 mx-auto text-tc-mastery" />
          <h1 className="font-heading text-2xl font-bold mt-3">INTELLIGENCE is not unlocked yet</h1>
          <p className="text-tc-sub mt-2">{authz.authorization.reason}</p>
          <p className="text-tc-muted text-sm mt-2">{authz.is_minor ? "Reach the required competency and ask your guardian to authorize INTELLIGENCE." : ""}</p>
          {authz.authorization.plan_required
            ? <button data-testid="intel-see-plans-btn" onClick={() => navigate("/pricing")} className="mt-5 rounded-lg bg-tc-coach text-tc-bg font-semibold px-5 py-2.5">See plans</button>
            : <button data-testid="intel-back-btn" onClick={() => navigate("/roadmap")} className="mt-5 rounded-lg bg-tc-coach text-tc-bg font-semibold px-5 py-2.5">View Roadmap</button>}
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const tiers = data?.tiers || { DESERVES_ATTENTION: [], DEVELOPING: [], WATCH: [] };
  const nothing = s && s.surfaced_total === 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-tc-mastery text-xs font-mono uppercase tracking-widest"><Radar className="w-3.5 h-3.5" /> INTELLIGENCE · CURRENT MARKETS</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">What deserves my attention?</h1>
          <p className="text-tc-sub mt-1 max-w-xl text-sm">TRADECRAFT scans the current market universe and surfaces only the few situations that meet the framework. Finding a market is not a recommendation to trade it.</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpPopover testid="intel-help"
            what="A market-attention system. It screens many live markets deterministically and surfaces a small shortlist worth your analysis — then hands off to ANALYST."
            why="Attention Priority ranks how worthy a situation is of your inspection under the TRADECRAFT framework. It is NOT a win probability or price prediction."
            how="Run a scan, review the shortlist, open any candidate into ANALYST, and Watch the ones you want to track. Ask 'Why not?' about any symbol that didn't surface." />
          <button data-testid="intel-scan-btn" onClick={runScan} disabled={scanning}
            className="rounded-lg bg-tc-mastery text-tc-bg font-bold px-5 py-2.5 hover:bg-tc-mastery/90 transition-colors flex items-center gap-2 disabled:opacity-60">
            {scanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</> : <><RefreshCw className="w-4 h-4" /> Scan markets</>}
          </button>
        </div>
      </div>

      {/* Summary */}
      {s && (
        <div className="tc-card p-4 flex flex-wrap gap-4 items-center" data-testid="intel-summary">
          <Stat label="Screened" value={s.screened} />
          <Stat label="Eligible" value={s.eligible} />
          <Stat label="Interesting" value={s.interesting} />
          <Stat label="Developing" value={s.developing} accent="text-tc-xp" />
          <Stat label="Deserves attention" value={s.deserves_attention} accent="text-tc-coach" />
          <div className="ml-auto text-[11px] font-mono text-tc-muted flex items-center gap-1"><Clock className="w-3 h-3" /> scanned {new Date(s.at).toLocaleTimeString()} · {s.latency_ms}ms · {s.rejected?.length || 0} rejected</div>
        </div>
      )}

      {nothing && (
        <div className="tc-card p-8 text-center text-tc-sub" data-testid="intel-nothing">
          <Activity className="w-7 h-7 mx-auto text-tc-muted" />
          <p className="mt-2 font-medium">Nothing currently deserves your attention.</p>
          <p className="text-tc-muted text-sm mt-1">That's a valid result — TRADECRAFT won't manufacture situations to look busy. Check back after the next scan.</p>
        </div>
      )}

      {Object.entries(tiers).map(([tier, cards]) => cards.length > 0 && (
        <div key={tier} className="space-y-3" data-testid={`intel-tier-${tier.toLowerCase()}`}>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${TIER_META[tier].dot}`} />
            <span className={`text-[11px] font-mono uppercase tracking-widest ${TIER_META[tier].cls.split(" ")[0]}`}>{TIER_META[tier].label}</span>
          </div>
          {cards.map((c) => (
            <CandidateCard key={c.candidate_id} c={c} tier={tier} opening={opening === c.candidate_id}
              onOpen={() => openCandidate(c.candidate_id)} onWatch={() => watch(c.candidate_id)} />
          ))}
        </div>
      ))}

      {/* Why not this market? */}
      <div className="tc-card p-5" data-testid="intel-whynot">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-tc-muted"><CircleHelp className="w-3.5 h-3.5" /> Why isn't a market on my list?</div>
        <div className="flex items-center gap-2 mt-2">
          <input data-testid="whynot-input" value={whyNot.symbol} onChange={(e) => setWhyNot((w) => ({ ...w, symbol: e.target.value.toUpperCase() }))}
            placeholder="e.g. BTC/USD" className="bg-tc-panel border border-white/10 rounded-lg px-3 py-2 text-sm text-tc-sub" />
          <button data-testid="whynot-btn" onClick={doWhyNot} className="rounded-lg border border-white/15 text-tc-sub px-4 py-2 text-sm hover:border-tc-mastery/40 hover:text-tc-mastery">Ask</button>
        </div>
        {whyNot.result && whyNot.result !== "loading" && (
          <p className="text-sm text-tc-sub mt-3" data-testid="whynot-result">{whyNot.result.reason} {whyNot.result.detail ? `(${whyNot.result.detail.join("; ")})` : ""}</p>
        )}
        {whyNot.result === "loading" && <div className="mt-3 text-tc-muted text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</div>}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-tc-txt" }) {
  return (
    <div>
      <div className={`font-heading text-2xl font-bold ${accent}`}>{value}</div>
      <div className="text-[10px] font-mono uppercase text-tc-muted">{label}</div>
    </div>
  );
}

function CandidateCard({ c, tier, opening, onOpen, onWatch }) {
  const up = c.lean === "long";
  const Icon = up ? TrendingUp : c.lean === "short" ? TrendingDown : Activity;
  return (
    <div className={`tc-card p-5 border ${TIER_META[tier].cls}`} data-testid={`intel-candidate-${c.symbol.replace("/", "-")}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${up ? "text-tc-bull" : c.lean === "short" ? "text-tc-bear" : "text-tc-muted"}`} />
            <span className="font-heading font-bold text-lg">{c.symbol}</span>
            <span className="text-[9px] font-mono px-2 py-0.5 rounded border border-white/10 text-tc-muted">{c.situation_type.replace(/_/g, " ")}</span>
            <span className="text-[9px] font-mono px-2 py-0.5 rounded border border-white/10 text-tc-sub">{c.actionability.replace(/_/g, " ")}</span>
          </div>
          <div className="mt-2 space-y-0.5">
            {(c.why_surfaced || []).slice(0, 4).map((w, i) => <div key={i} className="text-[12px] text-tc-sub">• {w}</div>)}
          </div>
          {c.conflicting_evidence?.length > 0 && (
            <div className="text-[12px] text-tc-bear mt-1 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {c.conflicting_evidence[0]}</div>
          )}
          <div className="text-[11px] font-mono text-tc-muted mt-2">
            Waiting for: {c.condition_maturity?.note} · {c.risk_geometry_summary?.risk_reward ? `${c.risk_geometry_summary.risk_reward}R` : "no R:R"} · updated {timeAgo(c.data_provenance?.fetched_at)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <div className="font-heading text-2xl font-bold text-tc-txt" data-testid="attention-priority">{c.attention_priority.total}</div>
            <div className="text-[9px] font-mono uppercase text-tc-muted">attention priority</div>
          </div>
          <div className="flex gap-2">
            <button data-testid={`watch-${c.symbol.replace("/", "-")}`} onClick={onWatch} className="rounded-lg border border-white/15 text-tc-sub px-3 py-1.5 text-[12px] hover:border-tc-mastery/40 hover:text-tc-mastery flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Watch</button>
            <button data-testid={`analyze-${c.symbol.replace("/", "-")}`} onClick={onOpen} disabled={opening} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-1.5 text-[12px] hover:bg-tc-coach/90 flex items-center gap-1 disabled:opacity-60">
              {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Analyze <ArrowRight className="w-3.5 h-3.5" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function timeAgo(epoch) {
  if (!epoch) return "—";
  const s = Math.max(0, Math.floor(Date.now() / 1000 - epoch));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}
